import { forwardRef, useId } from 'react'
import type { ChangeEvent, ComponentProps, ElementRef } from 'react'

import type { CSSProps, VariantProps } from 'style'

import { useRefCallback } from 'hooks/use-ref-callback'

import { TextAreaStyled } from './textarea.styled'

const NAME = 'TextArea'

type TextAreaVariants = VariantProps<typeof TextAreaStyled>
type TextAreaBaseProps = ComponentProps<typeof TextAreaStyled>
type TextAreaCustomProps = {
  onChangeText?: (val: string) => void
  dynamicHeight?: boolean
}
type TextAreaProps = TextAreaCustomProps &
  TextAreaBaseProps &
  TextAreaVariants &
  CSSProps

const TextArea = forwardRef<ElementRef<typeof TextAreaStyled>, TextAreaProps>(
  (props, forwardedRef) => {
    const {
      id: idProp,
      valid,
      onChange,
      onChangeText,
      dynamicHeight,
      ...rest
    } = props

    // Resize input area based on content
    const ref = useRefCallback<HTMLTextAreaElement | null>(textarea => {
      if (textarea && dynamicHeight) {
        const onInput = () => {
          textarea.style.height = '0'
          textarea.style.height = textarea.scrollHeight + 'px'
        }
        textarea.setAttribute(
          'style',
          'height:' + textarea.scrollHeight + 'px;overflow-y:hidden;',
        )
        textarea.addEventListener('input', onInput, false)
      }
    })

    const uid = useId()
    const id = idProp || uid
    const ariaInvalid = valid === false ? 'true' : 'false'

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      event.persist()
      onChange?.(event)
      onChangeText?.(`${event.target.value}`)
    }

    return (
      <TextAreaStyled
        {...rest}
        id={id}
        ref={r => {
          // @ts-ignore
          forwardedRef?.(r)
          ref(r)
        }}
        valid={valid}
        dynamicHeight={dynamicHeight}
        aria-invalid={ariaInvalid}
        onChange={handleChange}
      />
    )
  },
)

TextArea.displayName = NAME

export default TextArea
